import CustomNotification from 'controls/customNotification';
import RemoteEventsManager from 'core/remoteEventsManager';
import ErrorCodes from 'tools/errorCodes';
import Utils from "tools/utils";
import MultiToggle from 'controls/multiToggle';
import Cookies from 'core/cookies';
import Settings from "settings";
import React from "react";
import WidgetInfoContainer from "../../controls/react/widgetInfoContainer";
import State from 'tools/state';
import {loadQualifierDetails } from "../services/designer/qualifierWizard/wizardSave";
import { aggregationTypeDataSource } from './widgets/common/dataSources/aggregationTypeDataSource';
import LocalEventsManager from 'core/localEventsManager';

import "./widget.less";
import {
	areTheSame, legacyPeriods, periodToMilliseconds,
	renderIntoLegacy,
} from "controls/react/form/timePeriodSelector";
import {createRoot} from "react-dom/client";
import {UserPrefs} from "tools";
import Highcharts from "highcharts";
import {DownloadOutlined} from "@ant-design/icons";
import {exportToCSV} from "tools/exportHelpers/csvExporter";
import Renderer from "tools/renderer";
import {tooltipDataSource, tooltipDefaultValue} from "areas/metrics/components/MetricTooltipSelect/constants";
import {legendDataSource, legendDefaultValue} from "areas/metrics/components/MetricLegendSelect/constants";
import {
	generateLegendString
} from "areas/metrics/components/MetricLegendSelect/utils/generateLegendString";
import {generateLegendArray} from "areas/metrics/components/MetricLegendSelect/utils/generateLegendArray";
import {WidgetToolbox} from "areas/service-boards/widgetTollbox";
import classnames from "classnames";

const i18n = require('core/localization/localization').translator({
	'Y-axis': {
		no:'Y-akse'
	},
	'Dynamic': {
		no: 'Dynamikk'
	},
	"Zero": {
		no: 'Null'
	},
	'Export data to CSV': {no: 'Eksporter data til CSV'}
});

export let Widget = function (config) {
	Utils.apply(this, config);

	this.isDashboard = this.configuration.isDashboard || $('.html-shape-container').length > 0;
	if (State.currentApp?.dashboardDesigner?.props?.mode === 'designer') {
		this.isDashboardDesigner = true;
	}

	//new widgets does not use configuration property and store everything directly on config
	this.instanceConfiguration = JSON.parse(JSON.stringify(config.configuration || config));
};
export default Widget;

Widget.prototype = {
	hasExportCsv: false,
	// to support typification in ts
	type: null,
	dataItem: null,
	qualifier: null,
	qualifiers: [],
	hoverInfoQualifier: null,
	hasAggregateSelector: null,
	hasChartTypeSelector: null,
	hasMissingData: null,
	hasRegression: null,
	hasThreshold: null,
	defaultQualifierUnitType: null,
	hasMetricUnit: null,
	chartData: [],
	// ---

	getRequestPath() {
		if (this.sessionId) {
			return Settings.serverPath + 'sessions/' + this.sessionId + '/';
		}
		else {
			return Settings.serverPath;
		}
	},

	async loadUserPref() {
		if (this.isDashboard) {
			return;
		}
		const result = await UserPrefs.load('chart-settings_legend-tooltips');
		if (!result.success) {
			this.instanceConfiguration.tooltipTemplate ??= tooltipDataSource;
			this.instanceConfiguration.labelTemplate ??= tooltipDefaultValue;
			return;
		}
		this.instanceConfiguration.tooltipTemplate ??= JSON.parse(UserPrefs.get('tooltips-template-config') ?? JSON.stringify(tooltipDefaultValue));
		this.instanceConfiguration.labelTemplate ??= JSON.parse(UserPrefs.get('label-template-config') ?? JSON.stringify(legendDefaultValue));
	},

	/*
	* Handler function for creating the widget controls
	* */
	createCustomControls: async function (isSlaServiceHistory, callback) {
		this.isSlaServiceHistory = isSlaServiceHistory;
		if (!this.customControls) {
			return;
		}
		this.target = $(this.customControls.target);
		if (this.target.closest('.cw_freeform_widget').length) {
			this.target = this.target.closest('.k-window');
			this.isFreeServiceBoard = true;
		}

		if (this.target.hasClass('cw_widget')) {
			this.isServiceBoard = true;
		}

		this.hasAggregateSelector = ['single_graph', 'singlegraph', 'multi_graph_asset', 'multi_graph_assetgroup', 'graph_metric', 'metrics', "asset_metrics",
			"kpi_metrics", 'multigraph', 'asset_multigraph', 'kpi_multigraph',
			'metric-multi-graph-asset', 'metric-multi-graph-asset-group', 'metric-single-graph', 'service-single-graph',
			'service-history', 'health-single-graph', 'health-multi-graph', 'kpi-single-graph', 'kpi-multi-graph', 'kpi-history',
			'kpi-gauge', 'service-gauge', 'metric-gauge', 'health-gauge', ''].includes(this.type);

		await this.loadUserPref();

		let showTimeSelector = (!this.instanceConfiguration.timeSelectorPosition || this.instanceConfiguration.timeSelectorPosition !== 'NONE') && !this.instanceConfiguration.hideTimeSelector;

		setTimeout(() => {
			let actions;
			if (this.target.find('.k-window-actions').length) {
				actions = this.target.find('.k-window-actions');
			} else if (this.target.closest('.k-window').find('.k-window-actions').length) {
				actions = this.target.closest('.k-window').find('.k-window-actions');
			}
			if (actions) {
				var ariaToggle;
				if (actions.find('[aria-label="toggle"]').length) {
					ariaToggle = actions.find('[aria-label="toggle"]')
				} else if (actions.find('[aria-label="Toggle"]').length) {
					ariaToggle = actions.find('[aria-label="Toggle"]')
				}
			}
			if (ariaToggle) {
				if (!this.hasThreshold && !this.hasRegression && !this.hasMissingData && !this.hasChartTypeSelector) {
					ariaToggle.addClass('hide');
				} else {
					ariaToggle.removeClass('hide');
				}
			}

			if (this.type !== 'history') {
				this.truncateTitle();
			}

			callback?.()
		}, 300)

		const toolbarControls = [];
		if (this.hasInfoSign) {
			toolbarControls.push('info');
		}
		if (this.showToggleIcon) {
			toolbarControls.push('settings');
		}
		if (this.hasExportCsv) {
			toolbarControls.push('csvexport');
		}
		if (showTimeSelector && (this.hasTimeSelector || this.hasToggleTimeSelector)) {
			toolbarControls.push('timeselector');
		}
		let widgetId = this.id;
		if (this.isViewer) {
			if (this.isAssetDetails) {
				widgetId = $(this.renderTo).attr('id');
			} else {
				if (this.renderTo === 'cw_preview_widget') {
					//default opened widget
					widgetId = this.instanceConfiguration.serviceId;
				} else {
					//other spawned widgets
					widgetId = this.renderTo;
				}
			}
		}
		const containerClassNames = classnames('widget_toolbox_container', {'bottom-time-selector': this.instanceConfiguration.timeSelectorPosition === 'BOTTOM'})
		if (!this.target.find('.widget_toolbox_container').length || !this.widgetToolboxRoot) {
			if (this.target.find('.widget_toolbox_container').length) {
				this.target.find('.widget_toolbox_container').remove();
			}
			let container = this.target.find('.k-window-actions')

			if (container.length) {
				container.prepend('<div class="' + containerClassNames + '"></div>');
			} else if (this.target.find('.toolbar .toolbar__entry_set').length) {
				const containerWrapper = this.target.find('.toolbar .toolbar__entry_set');
				containerWrapper.prepend('<div class="toolbar__set-entry toolbar__set-entry_no-padding"><div class="' + containerClassNames + '"></div></div>');
			} else {
				this.target.append('<div class="' + containerClassNames + '"></div>');
			}
			this.widgetToolboxRoot = createRoot(this.target.find('.widget_toolbox_container')[0]);
		}

		this.widgetToolboxRoot.render(<WidgetToolbox widget={this} controls={toolbarControls} widgetId={widgetId} />)
	},

	exportCSV() {
		const data = this.chartData
			.filter(x => x.qualifierId);

		let index = 0;
		const resultData = [];
		while (data.some(x => x.data.length >= index + 1)) {
			data.forEach((x) => {
				if (!x.data[index]) {
					return;
				}
				let current = resultData.find(y => y.timestamp === x.data[index][0]);
				if (!current) {
					current = {timestamp: x.data[index][0]};
					data.forEach(x => current[x.qualifierId] = null)
					resultData.push(current);
				}
				current[x.qualifierId] = x.data[index][1];
			})
			index++;
		}
		resultData.sort((a, b) => a.timestamp - b.timestamp);
		const columns = [{field: 'timestamp', title: 'timestamp'}, ...data.map(x => ({field: x.qualifierId, title: x.name || `${x.toolTipAssetName}/${x.toolTipMetricName}`}))];
		exportToCSV(resultData, columns, {});
	},

	toggleSettingsOverlay() {
		if (this.target.find('.cw_widget_settings').is(':visible')) {
			this.target.find('.cw_widget_settings_overlay').css('display', 'none');
		} else {
			this.target.find('.cw_widget_settings_overlay').css('display', 'block');
		}
	},

	onNewTimeSelectorChanged(period) {
		if (!this.onWidgetPeriodChanged) {
			Object.assign(this.instanceConfiguration, period);
			this.reloadDataAndNotifyLegacy(period);
		} else {
			this.onWidgetPeriodChanged?.(period)
		}
	},

	truncateTitle() {
		let leftMostPosition, titleContainer, widgetContainer, cssLeft;

		if (this.isDashboard) {
			widgetContainer = this.target;
		} else if (this.widgetContentDiv) {
			//kendo window
			widgetContainer = this.widgetContentDiv.closest('.k-widget');
			if (!widgetContainer.length) {
				widgetContainer = this.widgetContentDiv.closest('.cw_widget');
			}
		} else if (this.target) {
			widgetContainer = this.target;
		}

		let periodToggle = widgetContainer.find('.period_multi_toggle');
		if (periodToggle.length) {
			leftMostPosition = periodToggle.position().left;
		}
		let toggleOptions = widgetContainer.find('.cw_toggle_options');
		if (toggleOptions.length && !this.isDashboard) {
			leftMostPosition -= 25;
			toggleOptions.css('margin-left', '30px');
		}
		if (this.isDashboard) {
			if (this.isDashboardDesigner) {
				leftMostPosition -= 40;

			} else {
				leftMostPosition -= 45;
			}
		}

		if (leftMostPosition) {
			if (this.isDashboard) {
				titleContainer = $(widgetContainer.find('.toolbar__entry_title').get(0));
				titleContainer.addClass('ellipsis');
			} else {
				titleContainer = widgetContainer.find('.k-window-title');
				if (!titleContainer.length) {
					titleContainer = widgetContainer.find('.cw_section_title');
				}
			}
			titleContainer.css('width', leftMostPosition - 20 + 'px');
			let titleText = titleContainer.text();
			titleContainer.attr('title', titleText);
		}
	},

	getMetricInfoUrl(widgetConfig) {
		const baseUrl = Settings.serverPath + 'accounts/' + (widgetConfig.accountId || Cookies.CeesoftCurrentAccountId) + '/services/' + widgetConfig.serviceId + '/';
		const url = baseUrl + 'elements/' + widgetConfig.serviceElementId + '/qualifiers/' + widgetConfig.serviceQualifierId + '/?update=false';
		return url;
	},

	async onInfoSignHover(e) {
		if (this.infoSignReactRoot) {
			return;
		}

		let data, infoObj, config, type, title;
		let widgetId = $(e.target).attr('widget-id');
		let widgets = State.currentApp.widgets;
		let currentWidget, infoUrl;
		if (widgets) {
			if (widgets.length) {
				for (let i = 0; i < widgets.length; i++) {
					if (widgets[i].id === widgetId) {
						currentWidget = widgets[i];
					}
				}
			} else {
				currentWidget = widgets[widgetId];
			}
		} else {
			currentWidget = this;
		}
		if (['service-gauge', 'metrics', 'gauge', 'service-single-graph'].indexOf(currentWidget.type) !== -1) {
			config = currentWidget.instanceConfiguration;
			infoObj = {
				accountId: config.accountId,
				serviceId: config.serviceId,
				serviceElementId: config.serviceElementId,
				serviceQualifierId: config.serviceQualifierId
			};
		} else {
			if (currentWidget.dataItem && currentWidget.dataItem.type === 'COLLECTOR') {
				//asset details collector
				config = currentWidget.dataItem;
				infoObj = {
					accountId: config.accountId,
					serviceId: config.parentId,
					serviceElementId: config.elementId,
					serviceQualifierId: config.qualifierId
				};
			} else if (currentWidget.type === 'graph_gauge' || currentWidget.type === 'metric-gauge') {
				//metric gauge
				data = currentWidget.qualifier || currentWidget.instanceConfiguration.metricsItem;
			} else if (['asset_gauge', 'kpi_gauge', 'health-gauge', 'kpi-gauge'].indexOf(currentWidget.type) !== -1) {
				//health/kpi gauge
				data = currentWidget.instanceConfiguration;
			}
			else if(currentWidget.type==='metric-single-graph'){
				data = currentWidget.qualifiers?.length ? currentWidget.qualifiers[0] : currentWidget.hoverInfoQualifier
			} else {
				data = currentWidget.hoverInfoQualifier;
			}
		}

		if(data) {
			delete data.isDashboard
		}

		if (infoObj) {
			type = 'request';
			infoUrl = this.getMetricInfoUrl(infoObj);
			data = await loadQualifierDetails(infoUrl);
		} else {
			data = this.deleteNotShowInfo(data);
			type = 'preloaded';
		}
		let coordinates = e.target.getBoundingClientRect();
		let flags = {
			hasAggregation: currentWidget.hasAggregateSelector,
			hasChartType: currentWidget.hasChartTypeSelector,
			hasMissingData: currentWidget.hasMissingData,
			hasRegression: currentWidget.hasRegression,
			hasThreshold: currentWidget.hasThreshold
		};
		let isAnyFlag = false;
		for (let key in flags) {
			if (flags[key]) {
				isAnyFlag = true;
			}
		}
		currentWidget.instanceConfiguration.aggregationType ??= currentWidget.qualifiers?.every(x => x.metricType == 'Boolean')
			? 'low'
			: 'high';

		this.infoSignReactRoot = createRoot(e.target)
		this.infoSignReactRoot.render(<WidgetInfoContainer
			data={data}
			type={type}
			chartSettings={currentWidget.instanceConfiguration}
			flags={flags}
			coordinates={coordinates}
			isAnyFlag={isAnyFlag}
		/>);
	},

	onInfoSignHoverOut(e) {
		this.hoverContainerTarget = e.target;
		//to make sure the container is destroyed; timing logic necessary for very fast hovering in and out
		let destroyContainer = setInterval(() => {
			this.infoSignReactRoot?.unmount();
			this.infoSignReactRoot = null;
		}, 200);
		setTimeout(() => clearInterval(destroyContainer), 400);
	},

	preProcessData(data) {
		return this.deleteNotShowInfo(data);
	},

	deleteNotShowInfo(data) {
		if (data) {
			delete data.accountId;
			delete data.agentId;
			delete data.assetId;
			delete data.conversion;
			delete data.configuration;
			delete data.customUnit;
			delete data.gaugeType;
			delete data.id;
			delete data.kpiAccountId;
			delete data.labelTemplate;
			delete data.metrics;
			delete data.monitorId;
			delete data.monitorType;
			delete data.profileId;
			delete data.ruleTypeId;
			delete data.sampleRateId;
			delete data.selected;
			delete data.qualifierDescription;
		}

		return data;
	},

	onGlobalClick: null,

	onPeriodMultiToggleClick: function (value, target) {
		if (value === 'CUSTOM') {
			let customTimeClass;
			if (this.customControls?.windowTarget) {
				this.target = this.target.closest('.k-window');
				customTimeClass = 'window-time-container';
			} else {
				customTimeClass = 'time-container';
			}
			if (this.target.find('.cw_custom_time_selector').length) {
				this.target.find('.cw_custom_time_selector').removeClass('hide');
				this.endTimePicker?.value(new Date());
			} else {
				var html = '';
				html += `<div class="cw_custom_time_selector ${customTimeClass}">`;
				html += '<div class="cw_time_input_container">';
				html += '<div>' + i18n('From') + '</div>';
				html += '<input class="cw_start_time cw_time_input"/>';
				html += '</div>';
				html += '<div class="cw_time_input_container">';
				html += '<div>' + i18n('To') + '</div>';
				html += '<input class="cw_end_time cw_time_input"/>';
				html += '</div>';
				html += '<button disabled class="k-button k-primary right" id="cw_apply_custom_time">' + i18n('Apply') + '</button>';
				html += '</div>';

				this.target.append(html);
				this.customTimeSelectorContainer = this.target.find('.cw_custom_time_selector');
				if(this.onGlobalClick == null) {
					this.onGlobalClick = (e) => {
						const datePickerContainer = document.getElementsByClassName("k-calendar-container");
						if(this.target[0].contains(e.target) || Array.from(datePickerContainer).some(x => x.contains(e.target)))
							return;
						this.target.find('.cw_custom_time_selector').addClass('hide');
					}
					document.body.addEventListener('click', this.onGlobalClick)
				}

				if (this.isServiceBoard || this.isDashboard) {
					this.target.find('.cw_custom_time_selector').css('top', '');
				}
				let viewerWindow = $('#cw_service_model_preview');
				if (viewerWindow.length) {
					this.target.find('.cw_custom_time_selector').css('right', '10px');
				}
				if (this.isSlaServiceHistory) {
					this.target.find('.cw_custom_time_selector').css({
						'position': 'absolute',
						'top': 'auto',
						'margin-top': '40px',
						'right': '20px'
					});
				}

				if (this.instanceConfiguration.hideHours) {
					this.startTimePicker = this.target.find(".cw_start_time").kendoDatePicker({
						format: Utils.datePatternConverter(Cookies.CeesoftUserDateFormat),
						value: this.instanceConfiguration?.startDate ? new Date(this.instanceConfiguration.startDate) : '',
						change: $.proxy(function() {
							let startValue = this.startTimePicker.value();
							let endValue = this.endTimePicker.value();
							if (endValue) {
								this.target.find('#cw_apply_custom_time').attr('disabled', false);
								if (endValue < startValue) {
									this.endTimePicker.value(startValue);
								}
							}
							this.endTimePicker.min(startValue);
						}, this)
					}).data('kendoDatePicker');

					this.endTimePicker = this.target.find('.cw_end_time').kendoDatePicker({
						format: Utils.datePatternConverter(Cookies.CeesoftUserDateFormat),
						value: this.instanceConfiguration?.endDate ? new Date(this.instanceConfiguration.endDate) : new Date(),
						change: $.proxy(function() {
							if (this.startTimePicker.value()) {
								this.target.find('#cw_apply_custom_time').attr('disabled', false);
							}
						}, this)
					}).data('kendoDatePicker');
				} else {
					this.startTimePicker = this.target.find(".cw_start_time").kendoDateTimePicker({
						format: Utils.datePatternConverter(Cookies.CeesoftUserDateTimeFormat),
						timeFormat: Utils.getTimeFormat(Cookies.CeesoftUserTimeFormat),
						value: this.instanceConfiguration?.startDate ? new Date(this.instanceConfiguration.startDate) : '',
						change: $.proxy(function() {
							let startValue = this.startTimePicker.value();
							let endValue = this.endTimePicker.value();
							if (endValue) {
								this.target.find('#cw_apply_custom_time').attr('disabled', false);
								if (endValue < startValue) {
									this.endTimePicker.value(startValue);
								}
							}
							this.endTimePicker.min(startValue);
						}, this)
					}).data('kendoDateTimePicker');

					this.endTimePicker = this.target.find('.cw_end_time').kendoDateTimePicker({
						format: Utils.datePatternConverter(Cookies.CeesoftUserDateTimeFormat),
						timeFormat: Utils.getTimeFormat(Cookies.CeesoftUserTimeFormat),
						value: this.instanceConfiguration?.endDate ? new Date(this.instanceConfiguration.endDate) : new Date(),
						change: $.proxy(function() {
							if (this.startTimePicker.value()) {
								this.target.find('#cw_apply_custom_time').attr('disabled', false);
							}
						}, this)
					}).data('kendoDateTimePicker');
				}

				this.target.find('#cw_apply_custom_time').on('click', $.proxy(this.onApplyCustomTime, this));
			}
		} else {
			if (this.startTimePicker && this.endTimePicker) {
				this.resetCustomTimePickers();
			}
			this.instanceConfiguration.period = value;

			this.reloadDataAndNotifyLegacy()
		}

		if (this.isViewer) {
			let currentHighlightWindow = $(target.closest('.k-window'));
			currentHighlightWindow.addClass('k-focused');
			$('body').on('click', (e) => {
				if ($(e.target).closest('.k-window')[0] !== currentHighlightWindow[0]) {
					currentHighlightWindow.removeClass('k-focused');
				}
			})
		}
		this.releaseEvents?.();
	},

	async onSettingsApply(value) {
		this.target.find('.cw_widget_settings').css('display', 'none');
		this.target.find('.cw_widget_settings_overlay').css('display', 'none');

		this.instanceConfiguration.ignoreMissingData = value.ignoreMissingData;
		this.instanceConfiguration.showRegression = value.showRegression;
		this.instanceConfiguration.showThreshold = value.showThreshold;
		this.instanceConfiguration.hideErrors = value.hideErrors;
		this.instanceConfiguration.chartType = value.chartType;
		this.instanceConfiguration.aggregationType = value.aggregationType;

		this.instanceConfiguration.maxType = value.maxType;
		this.instanceConfiguration.minType = value.minType;
		this.instanceConfiguration.maxValue = value.maxValue;
		this.instanceConfiguration.minValue = value.minValue;

		if (JSON.stringify(this.instanceConfiguration.labelTemplate) !== JSON.stringify(value.labelTemplate) || JSON.stringify(this.instanceConfiguration.tooltipTemplate) !== JSON.stringify(value.tooltipTemplate)) {
			this.instanceConfiguration.labelTemplate = value.labelTemplate;
			this.instanceConfiguration.tooltipTemplate = value.tooltipTemplate;
			const settings  =[{
				key: 'label-template-config',
				value: JSON.stringify(value.labelTemplate)
			}, {
				key: 'tooltips-template-config',
				value: JSON.stringify(value.tooltipTemplate)
			}]
			await UserPrefs.save('chart-settings_legend-tooltips', settings);
		}

		if (this.hasMetricUnit) {
			this.instanceConfiguration.unitType = value.unitType;
		}

		if (this.hasChartTypeSelector || this.hasAggregateSelector) {
			this.subscribe(true);
			this.getData({noSubscription: false});
		} else {
			this.getData({noSubscription: true});
		}

		this.fireConfigurationChanged();
	},

	resetCustomTimePickers() {
		this.startTimePicker.value('');
		this.startTimePicker.min(new Date(1900, 0, 1));
		this.startTimePicker.max(new Date(2100, 0, 1));
		this.endTimePicker.value('');
		this.endTimePicker.min(new Date(1900, 0, 1));
		this.endTimePicker.max(new Date(2100, 0, 1));
	},

	onApplyCustomTime: function (e) {
		this.instanceConfiguration.period = 'CUSTOM';
		this.instanceConfiguration.startDate = this.startTimePicker.value().getTime();
		if (this.instanceConfiguration.hideHours) {
			let endDate = this.endTimePicker.value();
			endDate.setDate(endDate.getDate() + 1);
			this.instanceConfiguration.endDate = endDate.getTime();
		} else {
			this.instanceConfiguration.endDate = this.endTimePicker.value().getTime();
		}

		this.reloadDataAndNotifyLegacy()
	},

	updateTimePeriod: function(timePeriod, timePeriodZoomed){
		const effectiveTimePeriod = timePeriodZoomed ?? timePeriod

		if(areTheSame(this.instanceConfiguration, effectiveTimePeriod) && (this.zoomed == !!timePeriodZoomed))
			return

		this.zoomed = !!timePeriodZoomed
		Object.assign(this.instanceConfiguration, effectiveTimePeriod)
		this.updateTimePeriodSelectorControlValue?.(effectiveTimePeriod)
		this.reloadDataAndNotifyLegacy(effectiveTimePeriod, false)
	},

	reloadDataAndNotifyLegacy: function(value, notify = true) {
		var event = {
			sender: {
				wrapper: this.target,
				value: this.instanceConfiguration.period
			},
			startDate: this.instanceConfiguration.startDate,
			endDate: this.instanceConfiguration.endDate
		};

		if (typeof this.customControls.change === 'function') {
			this.customControls.change.call(this, event);
		}
		if (typeof this.getData === 'function') {
			var forceTimeSelector = false;
			this.getData({noSubscription: true, forcedTimeSelector: forceTimeSelector});
		}
		this.zoomStartDate = null;
		this.zoomEndDate = null;
		this.zoomPeriod = null;
		this.unsubscribe();
		this.subscribe(true);
		this.releaseEvents?.();
		this.target.find('.cw_custom_time_selector').addClass('hide');
		this.fireConfigurationChanged();

		if (this.isSlaServiceHistory) {
			let configObj = {
				period: this.instanceConfiguration.period,
				startDate: this.instanceConfiguration.startDate,
				endDate: this.instanceConfiguration.endDate,
				intervalType: this.instanceConfiguration.intervalType,
				intervalValue: this.instanceConfiguration.intervalValue
			}
			LocalEventsManager.trigger('getActiveWidgetSettings', configObj);
		}
	},
	/*
	 * Handler function for getting the grid configuration to save along with the widget configration
	 */
	getConfiguration: function () {
		var config = {};
		if (this.hasConfiguration) {
			config = {
				sort: this.grid ? this.grid.dataSource.sort() || [] : [],
				filter: this.grid ? this.grid.dataSource.filter() || [] : [],
				columns: this.grid ? Utils.getGridColumns(this.grid) || [] : []
			};
			if (this.gridSearch) {
				let searchValue = this.gridSearch.getValue();
				config.searchValue = searchValue;
			}
		}

		return config;
	},

	saveDisabledSeries: function () {
		if (this.chart) {
			this.disabledSeries = [];
			for (var i = 0; i < this.chart.series.length; i++) {
				if (!this.chart.series[i].visible) {
					this.disabledSeries.push(i);
				}
			}
		}
	},

	restoreDisabledSeries: function () {
		if (this.disabledSeries && this.disabledSeries.length) {
			for (var i = 0; i < this.disabledSeries.length; i++) {
				this.chart.series[this.disabledSeries[i]].hide();
			}
		}
	},

	fireConfigurationChanged(){
		this.onConfigurationChanged && this.onConfigurationChanged(this.instanceConfiguration);
	},

	getPeriodIntervalConfig() {
		return {
			period: this.zoomPeriod ?? this.instanceConfiguration?.period,
			startDate: this.zoomStartDate ? new Date(this.zoomStartDate).getTime() : this.instanceConfiguration?.startDate,
			endDate: this.zoomEndDate ? new Date(this.zoomEndDate).getTime() : this.instanceConfiguration?.endDate,
			intervalType: this.instanceConfiguration?.intervalType,
			intervalValue: this.instanceConfiguration?.intervalValue
		}
	},

	generateMultigraphLabel: function (qualifierConfig, delimiter) {
		qualifierConfig = JSON.parse(JSON.stringify(qualifierConfig));

		if (qualifierConfig.configuration && qualifierConfig.configuration.description) {
			qualifierConfig.qualifierDescription = qualifierConfig.configuration.description;
		}

		return generateLegendString({
			data: qualifierConfig,
			template: this.instanceConfiguration.legendType === 'informational'
				? this.instanceConfiguration.informationalTemplate?.map(x => typeof x == 'object' ? x.id : x) ?? []
				: this.instanceConfiguration.labelTemplate?.map(x => typeof x == 'object' ? x.id : x) ?? [],
			extra: {
				timezone: this.instanceConfiguration['timezone']
			},
			delimiter: delimiter
		})
	},

	getTooltipArray: function (qualifierConfig, x, y) {
		if (qualifierConfig.configuration && qualifierConfig.configuration.description) {
			qualifierConfig.qualifierDescription = qualifierConfig.configuration.description;
		}

		if (qualifierConfig.configuration) {
			qualifierConfig = {...qualifierConfig, ...qualifierConfig.configuration};
		}
		return generateLegendArray({
			data: qualifierConfig,
			template: this.instanceConfiguration.tooltipTemplate ?? tooltipDataSource,
			extra: {
				timezone: this.instanceConfiguration['timezone']
			},
			x, y
		});
	},

	/**
	 * Triggered after widget resize
	 * @param {Object} event The resize event
	 * @param {Object} ui The UI element - see http://api.jqueryui.com/resizable/
	 */
	onResize: function (event, ui) {
		//State.currentApp.saveServiceBoard();
	},
	/**
	 * Gets widget height as string
	 * @return {String} Widget height (i.e '32px')
	 */
	getHeight: function () {
		return $('#' + this.id).height() + 'px';
	},
	/**
	 * Sets the widget height
	 * @param {String} height The height in px : '32px'
	 * @deprecated
	 */
	setHeight: function (height) {
		// TODO remove all the calls to this method
	},
	/**
	 * Displays an error message inside a widget
	 * @param {Object} message
	 */
	showErrorMessage: function (message, status , animationTime) {
		var widgetContentDiv;
		if (!this.renderTo) {
			widgetContentDiv = $('#' + this.id).find('.cw_section_content');
		} else {
			widgetContentDiv = $('#' + this.renderTo);
		}
		widgetContentDiv.empty().append('<div class="cw_section_placeholder"></div>');
		this.statusNotification = new CustomNotification({
			appendToElement: widgetContentDiv,
			message: message,
			status: status || 'error',
			animationTime: animationTime
		}).show();
	},

	hideErrorMessage: function () {
		if (this.statusNotification) {
			this.statusNotification.hide();
		}
	},

	getTitleNode: function () {
		let node = $('.cw_section_title', '#' + this.id).get(0);
		if (node) {
			return node;
		}
		const root = document.getElementById(this.id);
		if(!root)
			return null;

		let kWindow = $(root.closest('.k-window'));
		if (!kWindow.length) {
			kWindow = $('.toolbar', $(root));
		}


		if (kWindow.find('.free_title_container').length) {
			node = kWindow.find('.free_title_container').get(0);
		} else {
			const $div = $('<div></div>').addClass('free_title_container');
			if (kWindow.find('.toolbar__entry_spacer').length) {
				kWindow.find('.toolbar__entry_spacer')[0].after($div.get(0))
			}
			else{
				kWindow.append($div);
			}
			node = $div.get(0);
		}

		return node;
	},

	setTitle: function(el) {
		this.reactTitle = true;

		const node = this.getTitleNode();

		if (node) {
			const freeLayout = $(node).addClass('').hasClass('free_title_container')
			this.titleReactRoot = createRoot(node)

			this.titleReactRoot.render(React.cloneElement(el, {
				skipTitle: freeLayout
			}));
		}
	},

	unsetTitle: function () {
		this.titleReactRoot?.unmount()
	},

	setTitleIfEmpty(newTitle) {
		if (this.title) {
			return;
		}

		let titleTag = document.getElementById(this.id)
			.querySelector('.cw_section_title, .oe');

		if (titleTag != null) {
			titleTag.innerText = newTitle;
		}
	},

	/*
	 * Handler function for setting the error tooltip
	 * @param {Object} handler The jQuery handler where to bind the tooltip
	 * */
	setErrorToolTip: function (handler) {
		if (!this.tooltipOverview) {
			this.tooltipOverview = Renderer.errorToolTip(handler);
		}
	},
	/*
	 * Handler function for loading the error codes
	 * */
	loadErrorCodes: function () {
		var errorCodes = ErrorCodes;
		errorCodes.setOptions({
			callback: this.initComponent(),
			sessionId: this.sessionId
		}).load();
	},
	/**
	 * Unsubscribe widget
	 */
	unsubscribe: function () {
		if (this.subscriberId) {
			RemoteEventsManager.unsubscribe(this.subscriberId);
			this.subscriberId = null;
		} else {
		}
		this.isDataSourceSubscribed = false;
	},
	/**
	 * Called when one or multiple events are received from the server
	 * @param {Array} events The events
	 */
	handleEvents: function (events) {
		//if onEvent exists: call onEvent as we are just refreshing
		if (this.onEvents) {
			this.onEvents(events);
		} else if (this.onEvent) {
			this.onEvent(events);
		}
	},

	isDashboardMode: function(){
		return $('#' + this.id).hasClass('html-shape-container');
	},

	removeOldPoints: function(data, dataSet) {
		const period = periodToMilliseconds(this.instanceConfiguration);
		if (period) {
			const pointsToRemove = dataSet.data.filter(x => x.x < data.metric.t - period);
			pointsToRemove.forEach(x => dataSet.removePoint(x));
		}
	},

	isDashboardViewer: function(){
		return !this.isDashboardDesigner;
	},

	isWithoutHeader() {
		const toolbar = $('.html-shape-container').find('.toolbar');
		return !toolbar.length
			|| toolbar.css('display') === 'none';
	},

	getAxesFontSettings() {
		if (this.instanceConfiguration.font) {
			return {
				style: {
					color: this.instanceConfiguration.font.color,
					fontSize: this.instanceConfiguration.font.size
				}
			}
		}
		return { }
	},

	destroy: function () {
		$('.k-i-toggle', this.target).closest('.k-window-action').on('click', this.toggleSettingsOverlay);
		if(this.onGlobalClick){
			document.body.removeEventListener('click', this.onGlobalClick)
		}
		if (this.hoverContainerTarget) {
			this.infoSignReactRoot?.unmount();
		}
		this.titleReactRoot?.unmount()
		this.startTimePicker?.destroy();
		this.endTimePicker?.destroy();
		this.customTimeSelectorContainer?.remove();
		setTimeout(()=> {
			this.timePeriodRoot?.unmount()
		}, 0)
		this.settingsRoot?.unmount()

		this.unsubscribe();
	}
}

export function addZoom(chartConfig, onSelect, onReset) {
	chartConfig.chart ??= {};
	chartConfig.chart.events ??= {};

	const findContainer = (el) => {
		let innerEl = el;
		while (innerEl && innerEl.parentElement) {
			if (innerEl.id?.startsWith('cw_view_widget') || innerEl.id?.startsWith('cw_preview_widget') || innerEl.classList.contains('cw_section_content')) {
				return innerEl;
			}
			innerEl = innerEl.parentElement;
		}
		return el.parentElement.parentElement;
	}
	chartConfig.chart.zooming = {
		type: 'x',
		resetButton: {
			position: {
				align: 'left',
				x: 5,
				y: 5,
			},
			theme: {
				'stroke-width': 1,
				stroke: '#aaa',
				fill: '#fff',
				r: 0,
				states: {
					hover: {
						fill: '#eee'
					},
					select: {
						fill: '#ccc'
					}
				}
			}
		}
	};
	const loadBase = chartConfig.chart?.events?.load;
	chartConfig.chart.events.load = function() {
		loadBase?.apply(this, arguments);
		const container = findContainer(this.container);
		if (container.dataset['chart.zoomApplied'] === 'true') {
			this.showResetZoom();
			this.zoomOut = () => {
				onReset?.();
				container.dataset['chart.zoomApplied'] = 'false';
			}
		}
	};
	const selectionBase = chartConfig.chart?.events?.selection;
	chartConfig.chart.events.selection = function(ev) {
		const container = findContainer(this.container);
		selectionBase?.apply(this, arguments);
		onSelect?.(ev);
		// every render chart re-creates itself, and to keep this flag we need some permanent store for widget
		container.dataset['chart.zoomApplied'] = 'true';
	};
	Highcharts.setOptions({
		lang: {
			resetZoom: i18n('Reset'),
		}
	});
	return chartConfig;
}
